import { Box, Typography, Stack, useTheme, alpha } from "@mui/material";

/**
 * Compact stat card for dashboard metrics (currency / % / counts).
 *
 * `default` and `dense` render exactly as they always have — they are used on desktop
 * (Dashboard) and by ManagePaperTrade, so nothing about them changed.
 *
 * `hero` is an additive variant used only on phones: one headline number, optionally
 * with a `badge` pill beside it (Growth % paired with Net P/L instead of spending a
 * whole extra tile on it).
 */
export function DashboardMetricCard({
  label,
  value,
  sublabel,
  icon: Icon,
  accent = "primary",
  highlight = false,
  valueColor,
  dense = false,
  hero = false,
  badge = null,
  badgeColor,
}) {
  const theme = useTheme();
  const accentColor = theme.palette[accent]?.main || theme.palette.primary.main;
  const accentBg = alpha(accentColor, highlight ? 0.2 : 0.1);
  const iconSize = hero ? 40 : dense ? 24 : 36;
  const pillColor = badgeColor || accentColor;

  const valueNode = (
    <Typography
      variant={hero ? "h5" : dense ? "body1" : "h6"}
      fontWeight={800}
      title={hero && typeof value === "string" ? value : undefined}
      sx={{
        mt: hero || badge ? 0 : dense ? 0.25 : 0.5,
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
        color: valueColor || "inherit",
        ...(hero
          ? {
              minWidth: 0,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : {}),
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        borderRadius: dense ? 1.5 : 2,
        p: dense ? 1 : 1.75,
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${alpha(accentColor, 0.22)}`,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, ${alpha(accentColor, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 55%)`
            : `linear-gradient(145deg, ${alpha(accentColor, 0.08)} 0%, #fff 50%)`,
        boxShadow: dense ? "none" : `0 4px 20px ${alpha("#000", 0.06)}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": dense ? {} : {
          transform: "translateY(-2px)",
          boxShadow: `0 12px 28px ${alpha(accentColor, 0.12)}`,
        },
      }}
    >
      {Icon && (
        <Box
          sx={{
            position: "absolute",
            top: dense ? 6 : 10,
            right: dense ? 6 : 10,
            width: iconSize,
            height: iconSize,
            borderRadius: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: accentBg,
            color: accentColor,
          }}
        >
          <Icon sx={{ fontSize: hero ? 22 : dense ? 14 : 20 }} />
        </Box>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={800}
        sx={{
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontSize: dense ? "0.62rem" : undefined,
          // hero only: keep a long label from sliding under the icon. Default/dense
          // keep their original (unpadded) behaviour.
          ...(hero && Icon ? { pr: `${iconSize + 6}px`, display: "block" } : {}),
        }}
      >
        {label}
      </Typography>

      {badge == null && !hero ? (
        valueNode
      ) : (
        <Stack
          direction="row"
          alignItems="baseline"
          flexWrap="wrap"
          columnGap={0.75}
          rowGap={0.25}
          sx={{ mt: dense ? 0.25 : 0.5, pr: hero && Icon ? `${iconSize + 6}px` : 0 }}
        >
          {valueNode}
          {badge != null && (
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.125,
                borderRadius: 999,
                fontSize: "0.72rem",
                fontWeight: 800,
                lineHeight: 1.6,
                whiteSpace: "nowrap",
                bgcolor: alpha(pillColor, 0.14),
                color: pillColor,
              }}
            >
              {badge}
            </Box>
          )}
        </Stack>
      )}

      {sublabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: dense ? 0.25 : 0.5, display: "block", fontSize: dense ? "0.65rem" : undefined }}
        >
          {sublabel}
        </Typography>
      )}
    </Box>
  );
}
